import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { Clock, User, ArrowLeft } from 'lucide-react';
import { ImageWithFallback } from '../components/ImageWithFallback';
import toast from 'react-hot-toast';

interface Article {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  image_url: string;
  published_at: string;
  author: {
    name: string;
  };
}

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);

  const formatContent = (content: string) => {
    return content
      .split('\n')
      .map(line => {
        if (line.startsWith('# ')) {
          return `<h1 class="text-4xl font-bold mb-6">${line.substring(2)}</h1>`;
        }
        if (line.startsWith('## ')) {
          return `<h2 class="text-2xl font-bold mt-8 mb-4">${line.substring(3)}</h2>`;
        }
        if (line.startsWith('### ')) {
          return `<h3 class="text-xl font-semibold mt-6 mb-3">${line.substring(4)}</h3>`;
        }
        if (line.startsWith('* ')) {
          return `<li class="ml-6 mb-2">${line.substring(2)}</li>`;
        }
        if (line.trim() === '') {
          return '<br/>';
        }
        return `<p class="mb-4">${line}</p>`;
      })
      .join('\n');
  };

  useEffect(() => {
    async function loadArticle() {
      try {
        if (!slug) return;

        const { data, error } = await supabase
          .from('articles')
          .select(`
            id,
            title,
            content,
            excerpt,
            image_url,
            published_at,
            author_id
          `)
          .eq('slug', slug)
          .eq('published', true)
          .single();

        if (error) throw error;
        setArticle(data);
      } catch (error) {
        console.error('Error loading article:', error);
        toast.error('Article not found');
      } finally {
        setLoading(false);
      }
    }

    loadArticle();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading article...</div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col space-y-4">
        <h1 className="text-2xl font-bold text-gray-900">Article Not Found</h1>
        <p className="text-gray-600">The article you're looking for doesn't exist or has been removed.</p>
        <Link
          to="/blog"
          className="inline-flex items-center text-blue-600 hover:text-blue-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{article.title} - Plumbers.Guru Blog</title>
        <meta name="description" content={article.excerpt} />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/blog"
          className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Blog
        </Link>

        {article.image_url && (
          <ImageWithFallback
            src={article.image_url}
            alt={article.title}
            className="w-full h-64 md:h-96 object-cover rounded-lg mb-8"
            sizes="(min-width: 768px) 100vw, 100vw"
          />
        )}

        <h1 className="text-4xl font-bold text-gray-900 mb-4">{article.title}</h1>

        <div className="flex items-center text-gray-600 mb-8">
          <div className="flex items-center mr-6">
            <User className="w-4 h-4 mr-2" />
            <span>Plumbers.Guru Team</span>
          </div>
          <div className="flex items-center">
            <Clock className="w-4 h-4 mr-2" />
            <span>
              {new Date(article.published_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>
        </div>

        <div 
          className="prose prose-blue max-w-none article-content"
          dangerouslySetInnerHTML={{ __html: formatContent(article.content) }}
        />
      </div>
    </>
  );
}